import { RuntimeConfigVariable } from '../types/deployment';

type JsonValidationResult<T> = {
  parsedValue: T | null;
  error: string;
};

const buildSyntaxErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    return `Invalid JSON: ${error.message}`;
  }

  return 'Invalid JSON';
};

const parseJsonValue = <T,>(value: string, fallbackValue: T): JsonValidationResult<unknown> => {
  if (!value.trim()) {
    return {
      parsedValue: fallbackValue,
      error: '',
    };
  }

  try {
    return {
      parsedValue: JSON.parse(value),
      error: '',
    };
  } catch (error) {
    return {
      parsedValue: null,
      error: buildSyntaxErrorMessage(error),
    };
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

export const validateRuntimeConfigVariablesJson = (value: string): JsonValidationResult<RuntimeConfigVariable[]> => {
  const parsedResult = parseJsonValue(value, []);
  if (parsedResult.error) {
    return {
      parsedValue: null,
      error: parsedResult.error,
    };
  }

  if (!Array.isArray(parsedResult.parsedValue)) {
    return {
      parsedValue: null,
      error: 'Expected a JSON array.',
    };
  }

  return {
    parsedValue: parsedResult.parsedValue as RuntimeConfigVariable[],
    error: '',
  };
};

export const validatePipelineVariablesJson = (value: string): JsonValidationResult<Record<string, string>> => {
  const parsedResult = parseJsonValue(value, {});
  if (parsedResult.error) {
    return {
      parsedValue: null,
      error: parsedResult.error,
    };
  }

  if (!isPlainObject(parsedResult.parsedValue)) {
    return {
      parsedValue: null,
      error: 'Expected a JSON object.',
    };
  }

  const invalidEntry = Object.entries(parsedResult.parsedValue).find(([, entryValue]) => typeof entryValue !== 'string');
  if (invalidEntry) {
    return {
      parsedValue: null,
      error: `Value for "${invalidEntry[0]}" must be a string.`,
    };
  }

  return {
    parsedValue: parsedResult.parsedValue as Record<string, string>,
    error: '',
  };
};
